import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { FormLabel } from "../../components/form-label/FormLabel";
import { flowService } from "../../services/flowService";
import { chatbotService } from "../../services/chatbotService";
import { Flow, FlowStep, FlowStepType } from "../../types";
import { StepList } from "./components/StepList";
import { StepEditor } from "./components/StepEditor";
import { FlowMetaForm, FlowMetaState } from "./components/FlowMetaForm";

// Build a blank step of the given type
function makeStep(type: FlowStepType, order: number): FlowStep {
  const id = crypto.randomUUID();
  const base = { id, order };
  switch (type) {
    case "collect":
      return {
        ...base,
        type,
        field_key: "",
        field_type: "text",
        question: "",
        required: true,
      };
    case "decision":
      return {
        ...base,
        type,
        field_key: "",
        operator: "==",
        value: "",
        on_true: { end_flow: true, message: "Operazione conclusa." },
        on_false: { end_flow: true, message: "Operazione conclusa." },
      };
    case "tool_call":
      return {
        ...base,
        type,
        tool_name: "",
        input_map: {},
        output_field: "",
      };
    case "action":
      return { ...base, type, tool_name: "", input_map: {} };
    case "route":
      return { ...base, type, next_flow_id: "" };
    case "message":
      return { ...base, type, content: "" };
    case "end":
      return { ...base, type, content: "" };
  }
}

const DEFAULT_META: FlowMetaState = {
  name: "",
  description: "",
  channel: "whatsapp",
  webhook_url: "",
  first_step_id: "",
};

const withCopySuffix = (name: string): string => {
  const suffix = " (Copia)";
  const max = 100;
  const base =
    name.length + suffix.length > max ? name.slice(0, max - suffix.length) : name;
  return base + suffix;
};

export default function FlowBuilder() {
  const { flowId } = useParams<{ flowId: string }>();
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("tenantId") ?? "";
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateFrom = (
    location.state as { duplicateFrom?: Flow } | null
  )?.duplicateFrom;
  const isNew = flowId === "new" || !flowId;
  const isDuplicate = isNew && !!duplicateFrom;

  const [steps, setSteps] = useState<FlowStep[]>([]);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [flowMeta, setFlowMeta] = useState<FlowMetaState>(DEFAULT_META);
  const [otherFlows, setOtherFlows] = useState<Flow[]>([]);
  // Orphan = no chatbot of this tenant references this flow. Derived FE-side.
  const [isOrphan, setIsOrphan] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load existing flow
  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await flowService.getById(flowId!);
        const f = res.data.data;
        setSteps(f.steps);
        setFlowMeta({
          name: f.name,
          description: f.description ?? "",
          channel: f.channel,
          webhook_url: f.webhook_url ?? "",
          first_step_id: f.first_step_id,
        });
        // Load other flows for this tenant (for TransitionEditor routing)
        const fr = await flowService.list(f.tenant_id);
        setOtherFlows(fr.data.data.flows.filter((x) => x.id !== flowId));
        // Flag the flow as orphan if no chatbot of the tenant references it.
        const cbRes = await chatbotService.list(f.tenant_id);
        setIsOrphan(!cbRes.data.data.chatbots.some((c) => c.flow_id === flowId));
      } catch (e) {
        console.error(e);
        setError("Errore nel caricamento del flow");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [flowId, isNew]);

  // Load other flows when tenantId available (new flow mode)
  useEffect(() => {
    if (!isNew || !tenantId) return;
    flowService
      .list(tenantId)
      .then((res) => setOtherFlows(res.data.data.flows))
      .catch((e) => console.error(e));
  }, [isNew, tenantId]);

  // Pre-fill from source flow when duplicating. Step IDs are internal to the
  // flow, so copying them verbatim keeps first_step_id and transitions coherent.
  const duplicateInitialized = useRef(false);
  useEffect(() => {
    if (!isDuplicate || duplicateInitialized.current || !duplicateFrom) return;
    duplicateInitialized.current = true;
    setSteps(duplicateFrom.steps);
    setFlowMeta({
      name: withCopySuffix(duplicateFrom.name),
      description: duplicateFrom.description ?? "",
      channel: duplicateFrom.channel,
      webhook_url: duplicateFrom.webhook_url ?? "",
      first_step_id: duplicateFrom.first_step_id,
    });
  }, [isDuplicate, duplicateFrom]);

  const selectedStep = steps.find((s) => s.id === selectedStepId) ?? null;

  const handleAddStep = (type: FlowStepType) => {
    const maxOrder = steps.length > 0 ? Math.max(...steps.map((s) => s.order)) : -1;
    const newStep = makeStep(type, maxOrder + 1);
    setSteps((prev) => [...prev, newStep]);
    setSelectedStepId(newStep.id);
  };

  const handleRemoveStep = (id: string) => {
    setSteps((prev) => prev.filter((s) => s.id !== id));
    if (selectedStepId === id) setSelectedStepId(null);
  };

  // Riordino via drag & drop: riassegna order = posizione nella lista.
  // Equivalente in effetto alle vecchie frecce → il fall-through per ordine
  // sul BE resta invariato (solo un'interazione più diretta).
  const handleReorder = (orderedIds: string[]) => {
    setSteps((prev) =>
      prev.map((s) => {
        const idx = orderedIds.indexOf(s.id);
        return idx === -1 ? s : { ...s, order: idx };
      })
    );
  };

  const handleUpdateStep = useCallback((updated: FlowStep) => {
    setSteps((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  }, []);

  const handleMetaChange = useCallback((meta: FlowMetaState) => {
    setFlowMeta(meta);
  }, []);

  const validate = (): string | null => {
    if (!flowMeta.name.trim()) return "Il nome del flow è obbligatorio.";
    if (!flowMeta.first_step_id) return "Seleziona il primo step del flow.";
    if (!steps.find((s) => s.id === flowMeta.first_step_id))
      return "Il primo step selezionato non esiste più. Aggiorna la selezione.";
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    const body = {
      name: flowMeta.name,
      description: flowMeta.description || undefined,
      channel: flowMeta.channel,
      webhook_url: flowMeta.webhook_url || undefined,
      first_step_id: flowMeta.first_step_id,
      steps,
    };
    try {
      if (isNew) {
        await flowService.create({ ...body, tenant_id: tenantId });
        navigate(`/chatbot/flows?tenantId=${tenantId}`);
      } else {
        await flowService.update(flowId!, body);
        setSaveSuccess(true);
      }
    } catch (e) {
      console.error(e);
      setError("Errore durante il salvataggio del flow");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const backUrl = tenantId
    ? `/chatbot/flows?tenantId=${tenantId}`
    : "/chatbot/flows";

  return (
    <div className="container">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <FormLabel
          label={isDuplicate ? "Duplica Flow" : isNew ? "Nuovo Flow" : `Flow Builder`}
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          {!isNew && tenantId && (
            <Button
              variant="outlined"
              onClick={() =>
                navigate(
                  `/chatbot/monitoring/conversations?tenantId=${tenantId}&flowId=${flowId}`
                )
              }
              disabled={saving}
            >
              Vedi esecuzioni
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate(backUrl)} disabled={saving}>
            Indietro
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {isNew ? "Crea flow" : "Salva flow"}
          </Button>
        </Box>
      </Box>

      {!isNew && isOrphan && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Questo flow non è associato ad alcun chatbot. Associalo dal dettaglio di
          un chatbot per renderlo operativo.
        </Alert>
      )}
      {isDuplicate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Stai duplicando il flow <strong>{duplicateFrom?.name}</strong> — verrà
          creato un nuovo flow con gli stessi step. Rivedi nome e configurazione
          prima di salvare.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Flow salvato
        </Alert>
      )}

      {/* Metadata */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Informazioni flow
        </Typography>
        <FlowMetaForm meta={flowMeta} steps={steps} onChange={handleMetaChange} />
      </Paper>

      {/* 2-column layout */}
      <Box sx={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 2 }}>
        {/* Left — step list */}
        <Paper variant="outlined" sx={{ p: 2, alignSelf: "start" }}>
          <StepList
            steps={steps}
            selectedStepId={selectedStepId}
            onSelect={setSelectedStepId}
            onAdd={handleAddStep}
            onRemove={handleRemoveStep}
            onReorder={handleReorder}
          />
        </Paper>

        {/* Right — step editor */}
        <Paper variant="outlined" sx={{ p: 2, minHeight: 200 }}>
          {selectedStep ? (
            <StepEditor
              step={selectedStep}
              steps={steps}
              flows={otherFlows}
              onUpdate={handleUpdateStep}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 200,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Seleziona uno step dalla lista per modificarlo
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </div>
  );
}
